import type { NodeStatus, PipelineGraph, GraphNode } from '../lib/pipeline';

interface Props {
  graph: PipelineGraph;
  nodeStatus: Record<string, NodeStatus>;
}

type Lane = {
  title: string;
  helper: string;
  nodeIds: string[];
};

const RUNTIME_LANES: Lane[] = [
  { title: 'Plan', helper: 'Choose what to make', nodeIds: ['planner'] },
  { title: 'Context', helper: 'Design the slide arc', nodeIds: ['researcher'] },
  { title: 'Craft', helper: 'Hook + CTA specialists', nodeIds: ['hookSmith', 'ctaSmith'] },
  { title: 'Write', helper: 'Fill the middle slides', nodeIds: ['writer'] },
  { title: 'Gates', helper: 'Block weak or unsafe work', nodeIds: ['empathy', 'safety'] },
  { title: 'Refine', helper: 'Tighten and revise', nodeIds: ['editor', 'finalReader'] },
  { title: 'Design', helper: 'Render the carousel', nodeIds: ['designer'] },
  { title: 'Create', helper: 'Channel-ready outputs', nodeIds: ['linkedin', 'instagram'] },
  { title: 'Learn', helper: 'Feed future learning', nodeIds: ['analyst'] },
];

const REEL_LANES: Lane[] = [
  { title: 'Plan', helper: 'Choose what to make', nodeIds: ['planner'] },
  { title: 'Craft', helper: 'Hook + CTA specialists', nodeIds: ['reelHookSmith', 'reelCtaSmith'] },
  { title: 'Storyboard', helper: 'Compose scenes + infer mood', nodeIds: ['storyboard'] },
  { title: 'Gates', helper: 'Block weak or unsafe work', nodeIds: ['empathy', 'safety'] },
  { title: 'Refine', helper: 'Fix + tighten for spoken delivery', nodeIds: ['editor', 'reelTightener'] },
  { title: 'Direct', helper: 'Write the Remotion source', nodeIds: ['reelArtDirector'] },
  { title: 'Review', helper: 'Creative-director gate', nodeIds: ['reelFinalReader'] },
  { title: 'Audio', helper: 'VO per-scene or library music', nodeIds: ['reelVoiceSynthesizer', 'reelMusicComposer'] },
  { title: 'Render', helper: 'Compose the reel', nodeIds: ['reelComposer'] },
  { title: 'Learn', helper: 'Feed future learning', nodeIds: ['analyst'] },
];

const SETUP_LANES: Lane[] = [
  { title: 'Understand', helper: 'Parse the source', nodeIds: ['parser'] },
  { title: 'Direction', helper: 'Audience and voice', nodeIds: ['audience', 'voice'] },
  { title: 'Memory', helper: 'Personas and themes', nodeIds: ['persona', 'theme'] },
  { title: 'Identity', helper: 'Website and visual system', nodeIds: ['website', 'brandkit'] },
  { title: 'Proof', helper: 'Writing strategist · sample posts', nodeIds: ['sample'] },
];

export function PipelineGraphView({ graph, nodeStatus }: Props) {
  const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
  // Both reel and carousel content teams share graph.id='runtime'; the reel
  // team is identified by the presence of reel-only nodes (which the carousel
  // team doesn't have). v16 removed reelScriptWriter — `reelArtDirector` is
  // the stable reel-only marker that's existed since v9.
  const isReelTeam = nodesById.has('reelArtDirector');
  const lanes = graph.id === 'setup' ? SETUP_LANES : isReelTeam ? REEL_LANES : RUNTIME_LANES;

  return (
    <div className="rounded-[1.5rem] border border-border-color bg-background-dark p-3 md:p-4 overflow-x-auto scroll-thin">
      <div
        className="grid gap-2 items-stretch"
        style={{ gridTemplateColumns: `repeat(${lanes.length}, 150px)` }}
      >
        {lanes.map((lane, i) => (
          <LaneColumn
            key={lane.title}
            lane={lane}
            nodesById={nodesById}
            nodeStatus={nodeStatus}
            isLast={i === lanes.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function LaneColumn({ lane, nodesById, nodeStatus, isLast }: {
  lane: Lane;
  nodesById: Map<string, GraphNode>;
  nodeStatus: Record<string, NodeStatus>;
  isLast: boolean;
}) {
  return (
    <section className="relative rounded-2xl border border-border-color bg-surface/80 p-2.5 min-h-[200px]">
      {!isLast && (
        <div className="absolute top-1/2 -right-2 z-10 h-px w-4 bg-border-color">
          <span className="absolute -right-1 -top-[4px] h-2 w-2 rotate-45 border-r border-t border-border-color bg-transparent" />
        </div>
      )}
      <header className="mb-2.5 min-h-[44px]">
        <p className="font-display text-base font-bold tracking-tight leading-tight">{lane.title}</p>
        <p className="mt-0.5 text-[10px] text-muted leading-tight line-clamp-2">{lane.helper}</p>
      </header>
      <div className="space-y-1.5">
        {lane.nodeIds.map((id) => {
          const node = nodesById.get(id);
          if (!node) return null;
          return <FilterNode key={id} node={node} status={nodeStatus[id] ?? 'idle'} />;
        })}
      </div>
    </section>
  );
}

/** Turn an agent's CamelCase label into something human:
 *    "ReelVoiceSynthesizer" → "Voice synth"
 *    "ReelArtDirector"      → "Art director"
 *    "Empathy Critic"       → "Empathy critic"  (already-spaced labels pass through)
 *
 * The 'Reel' prefix is implicit context on this page and just adds noise. */
function prettyNodeLabel(label: string): string {
  // Already-spaced labels: just preserve as-is, lowercase non-first words.
  if (label.includes(' ')) return label;
  // Strip leading 'Reel'.
  const stripped = label.replace(/^Reel/, '');
  // Split CamelCase into words.
  const words = stripped.split(/(?=[A-Z])/).filter(Boolean);
  if (words.length === 0) return label;
  return words
    .map((w, i) => (i === 0 ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
    .join(' ');
}

function FilterNode({ node, status }: { node: GraphNode; status: NodeStatus }) {
  const tone = STATUS_TONE[status];
  return (
    <article className={`rounded-lg border p-2.5 transition-colors ${tone.card}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-display text-[13px] font-semibold tracking-tight text-text-primary leading-tight break-words min-w-0">
          {prettyNodeLabel(node.label)}
        </p>
        <span className={`shrink-0 h-2 w-2 rounded-full mt-1 ${tone.dot}`} />
      </div>
      {node.llm === 'yes' && (
        <span className="inline-block mt-2 rounded px-1.5 py-0 font-mono text-[9px] uppercase tracking-wider text-accent-cyan/80 bg-accent-cyan/10">LLM</span>
      )}
      {node.llm === 'mixed' && (
        <span className="inline-block mt-2 rounded px-1.5 py-0 font-mono text-[9px] uppercase tracking-wider text-muted bg-surface-2">Mixed</span>
      )}
    </article>
  );
}

const STATUS_TONE: Record<NodeStatus, { card: string; dot: string; pill: string }> = {
  idle: {
    card: 'border-border-color bg-background-dark/70',
    dot: 'bg-muted/50',
    pill: 'bg-surface-2 text-muted',
  },
  running: {
    card: 'border-accent-cyan bg-accent-cyan/10 shadow-[0_0_24px_rgba(6,182,212,0.18)]',
    dot: 'bg-accent-cyan animate-pulse',
    pill: 'bg-accent-cyan/20 text-accent-cyan',
  },
  done: {
    card: 'border-accent-emerald/60 bg-accent-emerald/10',
    dot: 'bg-accent-emerald',
    pill: 'bg-accent-emerald/20 text-accent-emerald',
  },
  failed: {
    card: 'border-accent-red/70 bg-accent-red/10',
    dot: 'bg-accent-red',
    pill: 'bg-accent-red/20 text-accent-red',
  },
};
